import {
  createNewDamage,
  getAllDamages,
  getTotalActiveDamages,
  getDamageById,
  listAllDamages,
  updateDamage,
  updateManyDamages,
  getDamageByName,
  getMatchingDamageError,
  getDamageByClassId,
  listAllUniqueDamages,
  getDamageByNameAndSeverity,
} from '@/api/damage/damage';
import { TDamageSchema } from '@/schemas/damage/damage.schema';
import { TCustomQueryProps } from '@/type/hooks.types';
import { ErrorResponse } from '@/utils/axios.http';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getSortedList } from './list-order';
import { EquipmentDmgFilterType } from '@/entities/equipment';
import { getTranslatedItem } from '@/utils/language.utils';
import { notify } from '@/utils/toast.utils';
import { NOTIFY } from '@/utils/constant.utils';

export const useCreateDamage = ({
  t,
  handleSuccess,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: TDamageSchema) => createNewDamage(data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['getAllDamages'] });
      queryClient.invalidateQueries({ queryKey: ['getTotalActiveDamages'] });
      queryClient.invalidateQueries({ queryKey: ['getMatchingDamageError'] });
      queryClient.invalidateQueries({
        queryKey: ['getDamageByNameAndSeverity'],
      });
      queryClient.invalidateQueries({ queryKey: ['listAllDamages'] });
      queryClient.invalidateQueries({ queryKey: ['listAllUniqueDamages'] });

      notify(t('damageCreatedSuccessfully'), NOTIFY.SUCCESS);

      handleSuccess?.(data);
    },
    onError: (error: ErrorResponse) => {
      notify(t('common:errorOccurredTryAgainLater'), NOTIFY.ERROR);
      console.error(error.error);
    },
  });
};

export const useGetMatchingDamageError = (query?: TDamageSchema) => {
  return useQuery({
    queryKey: ['getMatchingDamageError', query],
    queryFn: () => getMatchingDamageError(query),
  });
};

export const useListAllDamages = () => {
  return useQuery({
    queryKey: ['listAllDamages'],
    queryFn: () => listAllDamages(),
  });
};

export const useListAllUniqueDamages = () => {
  return useQuery({
    queryKey: ['listAllUniqueDamages'],
    queryFn: () => listAllUniqueDamages(),
  });
};

export const useGetModerationDamages = (classIdAsValue = true) => {
  const { data: allDamages, isLoading: isLoadingAllDamages } =
    useListAllDamages();

  const getUniqueDamageOptions = () => {
    // Process the data to get unique damage names without the "_number" suffix
    const uniqueDamageNames = new Set();
    const damageMap = new Map();

    // Process damage data to get unique names and colors
    allDamages?.data?.forEach((item: TDamageSchema) => {
      const translatedItem = getTranslatedItem(item?.['translatedDamages']);

      const baseName = (translatedItem?.name || item?.name)?.trim();

      if (baseName) {
        uniqueDamageNames.add(baseName);
        damageMap?.set(baseName, item?.color);
      }
    });

    // Create array of unique damage objects
    const uniqueDamages = Array.from(uniqueDamageNames).map((name) => ({
      name,
      color: damageMap.get(name),
    }));

    // Get unique damage select options
    const uniqueOptions = getSortedList(uniqueDamages, 'name', 'asc')?.map(
      (item: { name: string; color: string }) => {
        const translatedItem = getTranslatedItem(item?.['translatedDamages']);

        return {
          label: translatedItem?.name || item?.name,
          value: item?.name,
          color: item?.color,
        };
      },
    );

    return [...(uniqueOptions || [])];
  };

  const getSelectAllDamageOptions = () => {
    // Get all damage select options
    const allOptions = getSortedList(allDamages?.data, 'name', 'asc')?.map(
      (item: TDamageSchema) => {
        const translatedItem = getTranslatedItem(item?.['translatedDamages']);

        return {
          label: translatedItem?.name || item?.name,
          value: classIdAsValue ? `${item?.classId}` : item?.id,
          color: item?.color,
          severity: item?.severity,
        };
      },
    );

    return [...(allOptions || [])];
  };

  return {
    isLoading: isLoadingAllDamages,
    uniqueDamagesSelectOptions: getUniqueDamageOptions(),
    allDamagesSelectOptions: getSelectAllDamageOptions(),
  };
};

export const useGetAllDamages = (
  filter: EquipmentDmgFilterType,
  page: number,
  limit: number,
  sortBy?: string,
  sortDirection?: 'asc' | 'desc',
) => {
  return useQuery({
    queryKey: ['getAllDamages', filter, page, sortBy, sortDirection],
    queryFn: () => getAllDamages(filter, page, limit, sortBy, sortDirection),
  });
};

export const useGetTotalActiveDamages = () => {
  return useQuery({
    queryKey: ['getTotalActiveDamages'],
    queryFn: () => getTotalActiveDamages(),
  });
};

export const useGetDamageById = (id: string) => {
  return useQuery({
    queryKey: ['getDamageById', id],
    queryFn: () => getDamageById(id),
    enabled: !!id,
  });
};

export const useGetDamageByClassId = (classId: number) => {
  return useQuery({
    queryKey: ['getDamageByClassId', classId],
    queryFn: () => getDamageByClassId(classId),
    enabled: !!classId,
  });
};

export const useGetDamageByName = (name: string) => {
  return useQuery({
    queryKey: ['getDamageByName', name],
    queryFn: () => getDamageByName(name),
    enabled: !!name,
  });
};

export const useGetDamageByNameAndSeverity = (
  name: string,
  severity: number,
) => {
  return useQuery({
    queryKey: ['getDamageByNameAndSeverity', name, severity],
    queryFn: () => getDamageByNameAndSeverity(name, severity),
    enabled: !!name && !!severity,
  });
};

export const useGetUniqueDamageNamesSelectOptions = () => {
  const { data: allDamages, isLoading: isLoadingAllDamages } =
    useListAllUniqueDamages();

  const getAllDamageNamesSelectOptions = () => {
    if (!isLoadingAllDamages && allDamages && allDamages?.data?.length > 0) {
      return allDamages?.data?.map((damage: TDamageSchema) => {
        return {
          label: damage?.name,
          value: damage?.name,
        };
      });
    }

    return undefined;
  };

  return {
    getAllDamageNamesSelectOptions,
    isLoadingAllDamages,
  };
};

export const useUpdateDamage = ({
  t,
  handleSuccess,
  handleError,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (damage: TDamageSchema) => updateDamage(damage),
    onSuccess: (_, variables) => {
      const { id } = variables;

      queryClient.invalidateQueries({ queryKey: ['getAllDamages'] });
      queryClient.invalidateQueries({ queryKey: ['getTotalActiveDamages'] });
      queryClient.invalidateQueries({ queryKey: ['getMatchingDamageError'] });
      queryClient.invalidateQueries({
        queryKey: ['getDamageByNameAndSeverity'],
      });
      queryClient.invalidateQueries({ queryKey: ['getDamageById', id] });
      queryClient.invalidateQueries({ queryKey: ['listAllDamages'] });
      queryClient.invalidateQueries({ queryKey: ['listAllUniqueDamages'] });

      notify(t('damageUpdatedSuccessfully'), NOTIFY.SUCCESS);

      handleSuccess?.();
    },
    onError: (err: ErrorResponse) => {
      notify(t('common:errorOccurredTryAgainLater'), NOTIFY.ERROR);
      handleError?.(err);
      console.error('Error when updating damage:', err.error);
    },
  });
};

export const useUpdateManyDamages = ({
  t,
  handleSuccess,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (damages: TDamageSchema[]) => updateManyDamages(damages),
    onSuccess: (_, variables) => {
      for (const item of variables) {
        queryClient.invalidateQueries({
          queryKey: ['getDamageById', item?.id],
        });
      }

      queryClient.invalidateQueries({ queryKey: ['getAllDamages'] });
      queryClient.invalidateQueries({ queryKey: ['getTotalActiveDamages'] });
      queryClient.invalidateQueries({ queryKey: ['getMatchingDamageError'] });
      queryClient.invalidateQueries({
        queryKey: ['getDamageByNameAndSeverity'],
      });
      queryClient.invalidateQueries({ queryKey: ['listAllDamages'] });
      queryClient.invalidateQueries({ queryKey: ['listAllUniqueDamages'] });

      notify(
        variables.length === 0
          ? t('common:theseElementsLinkedToEntitiesNotDeactivable')
          : variables.length > 1
            ? t('damagesUpdatedSuccessfully')
            : t('damageUpdatedSuccessfully'),
        NOTIFY.SUCCESS,
      );

      handleSuccess?.();
    },
    onError: (error: ErrorResponse) => {
      notify(t('common:errorOccurredTryAgainLater'), NOTIFY.ERROR);
      console.error('Error when updating damages:', error);
    },
  });
};
